import * as s from '~js/utils/selectors';
import * as a from '~js/utils/attributes';
import * as c from '~js/utils/classes';
import { a11y } from '~js/components/a11y';
import {
  makeSwatches,
  flickitySmoothScrolling,
  wrapElements,
  removeLoadingClassFromLoadedImages,
} from '~js/utils/utils';
import { ProductVideo } from '~js/components/product-video';
import { ProductAddForm } from '~js/components/product-add-form';
import { SwatchesContainer } from '~js/components/swatches-container';
import { Tooltip } from '~js/components/tooltip';
import { Popout } from '~js/components/popout';
import { Drawer } from '~js/components/drawer';

const ids = {
  addToCartFormId: 'AddToCartForm--',
  addToCartId: 'AddToCart--',
};

const Flickity = themeVendor.Flickity

export class LoadQuickview {
  constructor(popup, pswpElement) {
    this.popup = popup;
    this.pswpElement = pswpElement;
    this.quickViewFoot = this.pswpElement.querySelector(s.selectors$M.quickViewFootInner);
    this.quickViewInner = this.pswpElement.querySelector(s.selectors$M.quickViewInner);
    this.product = this.pswpElement.querySelectorAll(s.selectors$M.product);
    this.flkty = [];
    this.videos = [];
    this.productForms = [];
    this.deferredMedias = this.pswpElement.querySelectorAll(s.selectors$M.deferredMedia);
    this.buttonsShopTheLookThumb = this.pswpElement.querySelectorAll(s.selectors$M.shopTheLookThumb);
    this.quickViewItemHolders = this.pswpElement.querySelectorAll(s.selectors$M.quickViewItemHolder);
    this.popupCloseButtons = this.quickViewInner.querySelectorAll(s.selectors$M.popupClose);
    this.a11y = a11y;

    this.prevent3dModelSubmitEvent = (event) => this.prevent3dModelSubmit(event);
    this.closeOnAnimationEndEvent = (event) => this.closeOnAnimationEnd(event);
    this.closeOnEscapeEvent = (event) => this.closeOnEscape(event);

    this.outerCloseEvent = (event) => {
      if (!this.quickViewInner.contains(event.target)) {
        // Check if quickview has drawer
        const drawer = this.quickViewInner.nextElementSibling;
        if (drawer && drawer.contains(event.target)) return;

        this.closePopup(event);
      }
    };

    this.product.forEach((item, index) => {
      const isQuickViewOnboarding = item.hasAttribute(a.attributes$v.quickViewOnboarding);

      if (!isQuickViewOnboarding) {
        this.initItems(item, index);
      }
    });

    this.init();
    this.initTooltips();
    this.initPopouts();

    // Check swatches containers height
    this.swatchesContainer = new SwatchesContainer(this.pswpElement);
  }

  /*
   * Init tooltips for swatches
   */
  initTooltips() {
    this.tooltips = this.pswpElement.querySelectorAll(s.selectors$M.tooltip);
    this.tooltips.forEach((tooltip) => {
      new Tooltip(tooltip);
    });
  }

  /*
   * Init popouts
   */
  initPopouts() {
    this.popoutElements = this.pswpElement.querySelectorAll(s.selectors$M.popout);
    this.popouts = {};

    this.popoutElements?.forEach((popout, index) => {
      this.popouts[index] = new Popout(popout);
    });
  }

  handleDraggable(slider, draggableStatus) {
    if (!slider) return;

    slider.options.draggable = Boolean(draggableStatus);
    slider.updateDraggable();
  }

  initItems(item, index) {
    this.addFormSuffix(item);
    this.initProductSlider(item, index);
    this.initProductVideo(item);
    this.initProductModel(item);
    this.initShopifyXrLaunch(item);

    // Init swatches
    makeSwatches(item);

    // Init drawer
    const drawerToggles = this.pswpElement.querySelectorAll(s.selectors$M.drawerToggle);
    if (drawerToggles.length) {
      new Drawer(item);
    }

    // Wrap tables
    wrapElements(item);

    const productForm = new ProductAddForm(item.parentNode);
    this.productForms.push(productForm);

    if (Shopify.PaymentButton) {
      Shopify.PaymentButton.init();
    }

    item.classList.remove(c.classes$C.isLoading);
  }

  init() {
    // Prevent 3d models button redirecting to cart page when enabling/disabling the model a couple of times
    document.addEventListener('submit', this.prevent3dModelSubmitEvent);

    // Custom closing events
    this.popupCloseButtons.forEach((popupClose) => {
      popupClose.addEventListener('keyup', (event) => {
        if (
          event.code === theme.keyboardKeys.ENTER ||
          event.code === theme.keyboardKeys.NUMPADENTER ||
          event.code === theme.keyboardKeys.SPACE
        ) {
          this.closePopup(event);
        }
      });

      popupClose.addEventListener('click', (event) => {
        this.closePopup(event);
      });
    });

    this.pswpElement.addEventListener('click', this.outerCloseEvent);

    document.dispatchEvent(new CustomEvent('theme:popup:open', { bubbles: true }));

    this.popup.listen('preventDragEvent', (e, isDown, preventObj) => {
      preventObj.prevent = false;
    });

    this.pswpElement.addEventListener('mousedown', () => {
      this.popup.framework.unbind(window, 'pointermove pointerup pointercancel', this.popup);
    });

    // Opening event
    this.popup.listen('initialZoomInEnd', () => {
      document.body.classList.add(c.classes$C.jsQuickViewVisible);

      this.a11y.trapFocus({
        container: this.quickViewInner,
      });
    });

    this.pswpElement.addEventListener('animationend', this.closeOnAnimationEndEvent);

    this.popup.listen('destroy', () => {
      if (this.flkty.length > 0) {
        requestAnimationFrame(() => {
          this.flkty.forEach((slider) => slider.pausePlayer());
        });
      }
      document.body.classList.remove(c.classes$C.jsQuickViewVisible);
      document.removeEventListener('keyup', this.closeOnEscapeEvent);
      document.addEventListener('keyup', this.closeOnEscapeEvent);
      this.pswpElement.removeEventListener('click', this.outerCloseEvent);
      this.pswpElement.removeEventListener('animationend', this.closeOnAnimationEndEvent);
      document.removeEventListener('submit', this.prevent3dModelSubmitEvent);

      this.deferredMedias.forEach((deferredMedia) => {
        // Remove the 'loaded' attribute so the videos will can load properly when we reopening the quickview
        deferredMedia.removeAttribute(a.attributes$v.loaded);

        // Pause videos on closing the popup
        const media = deferredMedia.closest(s.selectors$M.productMediaWrapper);
        media.dispatchEvent(new CustomEvent('theme:media:hidden'), { bubbles: true });
        media.classList.add(c.classes$C.mediaHidden);
      });
    });

    document.addEventListener('keyup', this.closeOnEscapeEvent);
    document.addEventListener('theme:cart:added', () => {
      if (this.pswpElement.classList.contains(c.classes$C.popupQuickView)) {
        this.pswpElement.classList.add(c.classes$C.popupQuickViewAnimateOut);
      }
    });

    this.animateInQuickview();

    // 'Shop the look' thumbnails nav
    this.initShopTheLookListeners();
  }

  initShopTheLookListeners() {
    this.buttonsShopTheLookThumb?.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();

        const thumb = event.target.matches(s.selectors$M.shopTheLookThumb)
          ? event.target
          : event.target.closest(s.selectors$M.shopTheLookThumb);
        const holder = this.pswpElement.querySelector(
          `[${a.attributes$v.hotspot}="${thumb.getAttribute(a.attributes$v.hotspotRef)}"]`
        );

        if (thumb.classList.contains(c.classes$C.isActive) || !holder) return;

        // Handle sliders
        if (this.flkty.length > 0) {
          requestAnimationFrame(() => {
            this.flkty.forEach((slider) => {
              slider.resize();

              const allMediaItems = this.quickViewInner.querySelectorAll(s.selectors$M.productMediaWrapper);

              // Pause all media
              if (allMediaItems.length) {
                allMediaItems.forEach((media) => {
                  media.dispatchEvent(new CustomEvent('theme:media:hidden'), { bubbles: true });
                  media.classList.add(c.classes$C.mediaHidden);
                });
              }
            });
          });
        }

        // Active Quick View item class toggle
        holder.classList.add(c.classes$C.isActive);

        this.quickViewItemHolders.forEach((element) => {
          if (element !== holder) {
            element.classList.remove(c.classes$C.isActive);
          }
        });
      });
    });
  }

  // Prevents the 3d model buttons submitting the form
  prevent3dModelSubmit(event) {
    if (event.submitter.closest(s.selectors$M.deferredMedia) && event.submitter.closest(s.selectors$M.productForm)) {
      event.preventDefault();
    }
  }

  closeQuickviewOnMobile() {
    if (
      window.innerWidth < window.theme.sizes.large &&
      document.body.classList.contains(c.classes$C.jsQuickViewVisible)
    ) {
      this.popup.close();
    }
  }

  animateInQuickview() {
    this.pswpElement.classList.add(c.classes$C.popupQuickViewAnimateIn);

    this.quickViewFoot.addEventListener('animationend', (event) => {
      this.handleAnimatedState(event);
    });

    // Mobile
    this.pswpElement.addEventListener('animationend', (event) => {
      this.handleAnimatedState(event, true);
    });
  }

  handleAnimatedState(event, isMobileAnimation = false) {
    if (event.animationName == 'quickViewAnimateInUp') {
      if (isMobileAnimation && window.innerWidth >= window.theme.sizes.small) {
        // Checks mobile animation but it's not mobile screen size
        return;
      }

      this.pswpElement.classList.add(c.classes$C.popupQuickViewAnimated);
      this.pswpElement.classList.remove(c.classes$C.popupQuickViewAnimateIn);
      document.body.classList.remove(c.classes$C.jsQuickViewFromCart); // Clear the class that we are adding in quick-view-popup.js when the animation ends

      removeLoadingClassFromLoadedImages(this.pswpElement); // Remove loading class from images
    }
  }

  closePopup(event) {
    event?.preventDefault();
    const isNavDrawerOpen = document.body.classList.contains(c.classes$C.drawerOpen);

    if (isNavDrawerOpen) {
      document.dispatchEvent(new CustomEvent('theme:drawer:closing', { bubbles: true }));
    }

    this.pswpElement.classList.add(c.classes$C.popupQuickViewAnimateOut); // Adding this class triggers the 'animationend' event which calls closeOnAnimationEndEvent()

    this.swatchesContainer.onUnload();
  }

  closeOnAnimationEnd(event) {
    if (event.animationName == 'quickViewAnimateOutRight' || event.animationName == 'quickViewAnimateOutDown') {
      this.popup.template.classList.remove(c.classes$C.popupQuickViewAnimateOut, c.classes$C.popupQuickViewAnimated);
      this.popup.close();

      if (this.productForms.length > 0) {
        this.productForms.forEach((form) => form.onUnload());
      }
    }
  }

  closeOnEscape(event) {
    const isQuickViewVisible = document.body.classList.contains(c.classes$C.jsQuickViewVisible);
    const isNotificationVisible = document.body.classList.contains(c.classes$C.notificationPopupVisible);

    if (event.code === theme.keyboardKeys.ESCAPE && isQuickViewVisible && !isNotificationVisible) {
      this.closePopup(event);
    }
  }

  initProductSlider(item, index) {
    const slider = item.querySelector(s.selectors$M.productMediaSlider);
    const mediaItems = item.querySelectorAll(s.selectors$M.productMediaWrapper);

    if (mediaItems.length > 1) {
      const itemSlider = new Flickity(slider, {
        wrapAround: true,
        cellAlign: 'left',
        pageDots: false,
        prevNextButtons: true,
        adaptiveHeight: false,
        pauseAutoPlayOnHover: false,
        selectedAttraction: 0.2,
        friction: 1,
        autoPlay: false,
        on: {
          ready: () => {
            slider.setAttribute(a.attributes$v.tabindex, '-1');

            // This resize should happen when the show animation of the PhotoSwipe starts and after PhotoSwipe adds the custom 'popup--quickview' class with the mainClass option.
            // This class is changing the slider width with CSS and looks like this is happening after the slider loads which is breaking it. That's why we need to call the resize() method here.
            requestAnimationFrame(() => {
              itemSlider.resize();
            });
          },
          settle: () => {
            const currentSlide = itemSlider.selectedElement;
            const mediaId = currentSlide.getAttribute(a.attributes$v.mediaId);

            currentSlide.setAttribute(a.attributes$v.tabindex, '0');

            itemSlider.cells.forEach((slide) => {
              if (slide.element === currentSlide) {
                return;
              }

              slide.element.setAttribute(a.attributes$v.tabindex, '-1');
            });

            this.switchMedia(item, mediaId);
          },
        },
      });

      this.flkty.push(itemSlider);

      // Toggle flickity draggable functionality based on media play/pause state
      if (mediaItems.length) {
        mediaItems.forEach((element) => {
          element.addEventListener('theme:media:play', () => {
            this.handleDraggable(this.flkty[index], false);
            element.closest(s.selectors$M.productMediaSlider).classList.add(c.classes$C.hasMediaActive);
          });

          element.addEventListener('theme:media:pause', () => {
            this.handleDraggable(this.flkty[index], true);
            element.closest(s.selectors$M.productMediaSlider).classList.remove(c.classes$C.hasMediaActive);
          });
        });
      }

      // iOS smooth scrolling fix
      flickitySmoothScrolling(slider);
    }
  }

  switchMedia(item, mediaId) {
    const allMediaItems = this.quickViewInner.querySelectorAll(s.selectors$M.productMediaWrapper);
    const selectedMedia = item.querySelector(
      `${s.selectors$M.productMediaWrapper}[${a.attributes$v.mediaId}="${mediaId}"]`
    );
    const isFocusEnabled = !document.body.classList.contains(c.classes$C.noOutline);

    // Pause other media
    if (allMediaItems.length) {
      allMediaItems.forEach((media) => {
        media.dispatchEvent(new CustomEvent('theme:media:hidden'), { bubbles: true });
        media.classList.add(c.classes$C.mediaHidden);
      });
    }

    if (isFocusEnabled) {
      selectedMedia.focus();
    }

    selectedMedia.closest(s.selectors$M.productMediaSlider).classList.remove(c.classes$C.hasMediaActive);
    selectedMedia.classList.remove(c.classes$C.mediaHidden);
    selectedMedia.dispatchEvent(new CustomEvent('theme:media:visible'), { bubbles: true });

    // If media is not loaded, trigger poster button click to load it
    const deferredMedia = selectedMedia.querySelector(s.selectors$M.deferredMedia);
    if (deferredMedia && deferredMedia.getAttribute(a.attributes$v.loaded) !== 'true') {
      selectedMedia.querySelector(s.selectors$M.deferredMediaButton).dispatchEvent(new Event('click'));
    }
  }

  initProductVideo(item) {
    const videos = new ProductVideo(item);

    this.videos.push(videos);
  }

  initProductModel(item) {
    const sectionId = item.getAttribute(a.attributes$v.sectionId);
    const modelItems = item.querySelectorAll(s.selectors$M.productModel);

    if (modelItems.length) {
      modelItems.forEach((element) => {
        theme.ProductModel.init(element, sectionId);
      });
    }
  }

  initShopifyXrLaunch(item) {
    document.addEventListener('shopify_xr_launch', () => {
      const currentMedia = item.querySelector(`${s.selectors$M.productModel}:not(.${c.classes$C.mediaHidden})`);
      currentMedia.dispatchEvent(new CustomEvent('xrLaunch'));
    });
  }

  addFormSuffix(item) {
    const sectionId = item.getAttribute(a.attributes$v.sectionId);
    const productObject = JSON.parse(item.querySelector(s.selectors$M.productJSON).innerHTML);

    const formSuffix = `${sectionId}-${productObject.handle}`;
    const productForm = item.querySelector(s.selectors$M.productForm);
    const addToCart = item.querySelector(s.selectors$M.addToCart);

    productForm.setAttribute(a.attributes$v.id, ids.addToCartFormId + formSuffix);
    addToCart.setAttribute(a.attributes$v.id, ids.addToCartId + formSuffix);
  }
}